import { useState, useEffect } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	Button,
	TextField,
	Box,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Typography,
	IconButton,
	Grid,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { majorApi, MajorSchema, UpdateMajorSchema } from '../../services';
import { useErrorHandler } from '../../lib/errorHandler';
import { useAppSelector } from '../../store/hooks';
import z from 'zod';
import { DIALOG_SETTINGS, FULLSCREEN_DIALOG_PAPER_SX, FULLSCREEN_DIALOG_CONTENT_SX } from '../../utils/responsive';
import { useResponsiveDialog, useEntityDialog } from './hooks';
import { DialogFormActions, StatisticsPanel } from './base';

type Major = z.infer<typeof MajorSchema>;

type FormData = {
	maNganh: string;
	tenNganh: string;
	maKhoa: string;
	doUuTien: number;
	chiTieu: Record<string, number>;
};

interface MajorDialogProps {
	open: boolean;
	onClose: () => void;
	major?: Major | null;
	mode: 'create' | 'edit' | 'view';
}

export default function MajorDialog({ open, onClose, major, mode }: MajorDialogProps) {
	const { showError } = useErrorHandler();

	// Responsive and mode helpers
	const { isMobile, isReadOnly, isEditMode } = useResponsiveDialog({ mode });

	const [formData, setFormData] = useState<FormData>({
		maNganh: '',
		tenNganh: '',
		maKhoa: '',
		doUuTien: 0,
		chiTieu: {},
	});

	const faculties = useAppSelector((state) => state.filters.faculties);

	// Entity mutations
	const { isLoading, handleCreate, handleUpdate } = useEntityDialog<
		{ maNganh: string; tenNganh: string; maKhoa: string; doUuTien: number; chiTieu: Record<string, number> },
		z.infer<typeof UpdateMajorSchema>
	>({
		entityName: 'ngành',
		queryKeys: [['majors']],
		createFn: majorApi.createMajor,
		updateFn: (id, data) => majorApi.updateMajor(id, data),
		onSuccess: onClose,
	});

	// Fetch major statistics for view/edit mode (filtered by faculty if selected)
	const selectedFaculty = useAppSelector((state) => state.filters.selectedFaculty);
	const { data: majorStatsData, isLoading: isLoadingStats } = useQuery({
		queryKey: ['major', 'stats', major?.maNganh, selectedFaculty],
		queryFn: async () => {
			if (!major?.maNganh) return null;
			const params = new URLSearchParams();
			params.set('maNganh', major.maNganh);
			params.set('limit', '1');
			if (selectedFaculty) {
				params.set('maKhoa', selectedFaculty);
			}
			const response = await majorApi.getMajorStats(params);
			return response.data?.stats?.[0] || null;
		},
		enabled: open && !!major?.maNganh && (mode === 'view' || mode === 'edit'),
	});

	useEffect(() => {
		if ((mode === 'edit' || mode === 'view') && major) {
			setFormData({
				maNganh: major.maNganh || '',
				tenNganh: major.tenNganh || '',
				maKhoa: major.maKhoa || '',
				doUuTien: major.doUuTien || 0,
				chiTieu: major.chiTieu || {},
			});
		} else {
			setFormData({
				maNganh: '',
				tenNganh: '',
				maKhoa: '',
				doUuTien: 0,
				chiTieu: {},
			});
		}
	}, [major, mode, open]);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		e.stopPropagation();

		if (isLoading || isReadOnly) return;

		if (!formData.maNganh.trim() || !formData.tenNganh.trim() || !formData.maKhoa.trim()) {
			showError('Vui lòng điền đầy đủ thông tin');
			return;
		}

		if (mode === 'create') {
			handleCreate({
				maNganh: formData.maNganh,
				tenNganh: formData.tenNganh,
				maKhoa: formData.maKhoa,
				doUuTien: 0,
				chiTieu: formData.chiTieu,
			});
		} else if (mode === 'edit' && major) {
			handleUpdate(major.maNganh, {
				tenNganh: formData.tenNganh,
				maKhoa: formData.maKhoa,
				doUuTien: formData.doUuTien,
				chiTieu: formData.chiTieu,
			});
		}
	};

	const handleChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
		setFormData(prev => ({
			...prev,
			[field]: e.target.value,
		}));
	};

	const handleAddYear = () => {
		const currentYear = new Date().getFullYear();
		const nextYear = currentYear + Object.keys(formData.chiTieu).length;
		setFormData(prev => ({
			...prev,
			chiTieu: {
				...prev.chiTieu,
				[nextYear.toString()]: 0,
			},
		}));
	};

	const handleRemoveYear = (year: string) => {
		setFormData(prev => {
			const newChiTieu = { ...prev.chiTieu };
			delete newChiTieu[year];
			return {
				...prev,
				chiTieu: newChiTieu,
			};
		});
	};

	const handleQuotaChange = (year: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(e.target.value) || 0;
		setFormData(prev => ({
			...prev,
			chiTieu: {
				...prev.chiTieu,
				[year]: value,
			},
		}));
	};

	const getDialogTitle = () => {
		switch (mode) {
			case 'create':
				return 'Thêm ngành mới';
			case 'edit':
				return 'Chỉnh sửa ngành';
			case 'view':
				return 'Chi tiết ngành';
		}
	};

	const getFacultyName = (maKhoa: string) => {
		const faculty = faculties.find(f => f.value === maKhoa);
		return faculty?.label || maKhoa;
	};

	// Prepare extra column for statistics panel (quota)
	const extraColumns = [
		{
			key: 'quota',
			label: 'Chỉ tiêu',
			render: (year: string) => formData.chiTieu[year]?.toLocaleString() || '-',
		},
	];

	// View mode render
	if (isReadOnly) {
		return (
			<Dialog
				open={open}
				onClose={onClose}
				maxWidth={DIALOG_SETTINGS.FORM.MAX_WIDTH}
				fullWidth
				fullScreen={isMobile}
				PaperProps={{
					sx: isMobile ? FULLSCREEN_DIALOG_PAPER_SX : { p: DIALOG_SETTINGS.TABLET_PADDING },
				}}
			>
				<DialogTitle sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
					{isMobile && (
						<IconButton aria-label="back" onClick={onClose} edge="start" sx={{ mr: 1 }}>
							<ArrowBackIcon />
						</IconButton>
					)}
					{getDialogTitle()}
				</DialogTitle>
				<DialogContent sx={isMobile ? FULLSCREEN_DIALOG_CONTENT_SX : undefined}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
						<TextField
							label="Mã ngành"
							value={formData.maNganh}
							fullWidth
							slotProps={{ input: { readOnly: true } }}
						/>
						<TextField
							label="Tên ngành"
							value={formData.tenNganh}
							fullWidth
							slotProps={{ input: { readOnly: true } }}
						/>
						<TextField
							label="Khoa"
							value={getFacultyName(formData.maKhoa)}
							fullWidth
							slotProps={{ input: { readOnly: true } }}
						/>
						{majorStatsData && (
							<StatisticsPanel
								yearData={majorStatsData.yearData || {}}
								isLoading={isLoadingStats}
								extraColumns={extraColumns}
							/>
						)}
					</Box>
				</DialogContent>
				<DialogFormActions
					mode={mode}
					isLoading={isLoading}
					isReadOnly={isReadOnly}
					isMobile={isMobile}
					onClose={onClose}
				/>
			</Dialog>
		);
	}

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth={DIALOG_SETTINGS.FORM.MAX_WIDTH}
			fullWidth
			fullScreen={isMobile}
			PaperProps={{
				sx: isMobile ? FULLSCREEN_DIALOG_PAPER_SX : { p: DIALOG_SETTINGS.TABLET_PADDING },
			}}
		>
			<form onSubmit={handleSubmit} style={isMobile ? { display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' } : undefined}>
				<DialogTitle sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1 }}>
					{isMobile && (
						<IconButton
							aria-label="back"
							onClick={onClose}
							edge="start"
							disabled={isLoading}
							sx={{ mr: 1 }}
						>
							<ArrowBackIcon />
						</IconButton>
					)}
					{getDialogTitle()}
				</DialogTitle>
				<DialogContent sx={isMobile ? FULLSCREEN_DIALOG_CONTENT_SX : undefined}>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
						<TextField
							label="Mã ngành"
							value={formData.maNganh}
							onChange={handleChange('maNganh')}
							disabled={isEditMode || isLoading}
							required
							fullWidth
						/>
						<TextField
							label="Tên ngành"
							value={formData.tenNganh}
							onChange={handleChange('tenNganh')}
							disabled={isLoading}
							required
							fullWidth
						/>
						<FormControl fullWidth required>
							<InputLabel>Khoa</InputLabel>
							<Select
								value={formData.maKhoa}
								label="Khoa"
								onChange={(e) => setFormData(prev => ({ ...prev, maKhoa: e.target.value }))}
								disabled={isLoading}
							>
								{faculties.map((faculty) => (
									<MenuItem key={faculty.value} value={faculty.value}>
										{faculty.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>

						<Box sx={{ mt: 2 }}>
							<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
								<Typography variant="subtitle2" color="text.secondary">
									Chỉ tiêu theo năm
								</Typography>
								<Button
									size="small"
									startIcon={<AddIcon />}
									onClick={handleAddYear}
									disabled={isLoading}
								>
									Thêm năm
								</Button>
							</Box>

							{Object.keys(formData.chiTieu).length === 0 ? (
								<Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 2, textAlign: 'center' }}>
									Chưa có chỉ tiêu nào. Click "Thêm năm" để bắt đầu.
								</Typography>
							) : (
								<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
									{Object.entries(formData.chiTieu)
										.sort(([yearA], [yearB]) => parseInt(yearB) - parseInt(yearA))
										.map(([year, quota]) => (
											<Grid container spacing={1} key={year} alignItems="center">
												<Grid item xs={5}>
													<TextField
														label="Năm"
														value={year}
														onChange={(e) => {
															const newYear = e.target.value;
															if (newYear !== year) {
																setFormData(prev => {
																	const newChiTieu = { ...prev.chiTieu };
																	delete newChiTieu[year];
																	newChiTieu[newYear] = quota;
																	return { ...prev, chiTieu: newChiTieu };
																});
															}
														}}
														disabled={isLoading}
														fullWidth
														size="small"
														type="number"
													/>
												</Grid>
												<Grid item xs={6}>
													<TextField
														label="Chỉ tiêu"
														value={quota}
														onChange={handleQuotaChange(year)}
														disabled={isLoading}
														fullWidth
														size="small"
														type="number"
														inputProps={{ min: 0 }}
													/>
												</Grid>
												<Grid item xs={1}>
													<IconButton
														size="small"
														onClick={() => handleRemoveYear(year)}
														disabled={isLoading}
														color="error"
													>
														<DeleteIcon fontSize="small" />
													</IconButton>
												</Grid>
											</Grid>
										))}
								</Box>
							)}
						</Box>
					</Box>
				</DialogContent>
				<DialogFormActions
					mode={mode}
					isLoading={isLoading}
					isReadOnly={isReadOnly}
					isMobile={isMobile}
					onClose={onClose}
				/>
			</form>
		</Dialog>
	);
}